import {
  deleteUserMutation,
  updateUserMutation,
} from '@/api/users/usersMutations';
import type { UsersFilters } from '@/api/users/usersQueries';
import { getUsersQuery } from '@/api/users/usersQueries';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import type { User } from '@/domain/User';
import { formatDate } from '@/lib/utils/dateUtils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { EditUserRoleDialog } from './EditUserRoleDialog';
import { UsersRowFilters } from './UsersRowFilters';

const PER_PAGE = 15;
const INITIAL_USERS_FILTERS: UsersFilters = { per_page: PER_PAGE };

export default function UsersRow({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<
    (User & { clinic?: { id: number; name: string } }) | null
  >(null);
  const [filters, setFilters] = useState<UsersFilters>(INITIAL_USERS_FILTERS);
  const [filtersResetKey, setFiltersResetKey] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);

  const handleAccordionOpen = useCallback(() => {
    setFilters(INITIAL_USERS_FILTERS);
    setCurrentPage(1);
    setFiltersResetKey((k) => k + 1);
  }, []);

  const { data, isLoading } = useQuery(getUsersQuery(currentPage, filters));

  const users = data?.data ?? [];
  const meta = data?.meta;

  const updateMutation = useMutation({
    ...updateUserMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDialogOpen(false);
      setEditingUser(null);
      toast.success('User updated successfully');
    },
  });

  const deleteMutation = useMutation({
    ...deleteUserMutation(deletingUser?.id ?? 0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteDialogOpen(false);
      setDeletingUser(null);
      toast.success('User deleted successfully');
    },
  });

  const handleDelete = (user: User) => {
    setDeletingUser(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deletingUser) {
      deleteMutation.mutate();
    }
  };

  const handleEdit = (
    user: User & { clinic?: { id: number; name: string } },
  ) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  const handleSubmit = (userId: number, role: string) => {
    updateMutation.mutate({
      id: userId,
      role,
    });
  };

  const handleFiltersChange = (newFilters: UsersFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  return (
    <>
      <DashboardTableAccordion
        value="Users"
        title="Users"
        accordionOpenValue={accordionOpenValue}
        onOpen={handleAccordionOpen}
        data={users}
        isLoading={isLoading}
        emptyMessage="No users found"
        loadingMessage="Loading..."
        paginationMeta={meta}
        onPageChange={setCurrentPage}
        tableWrapper="card"
        filtersComponent={
          <UsersRowFilters
            key={filtersResetKey}
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />
        }
        renderHeader={() => (
          <>
            <TableHead className="w-16">Flag</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Change role</TableHead>
            <TableHead>Date created</TableHead>
            <TableHead className="w-48 text-right" />
          </>
        )}
        renderRow={(user) => (
          <>
            <TableCell>0</TableCell>
            <TableCell>
              {user.name} {user.surname}
            </TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell>{user.phone}</TableCell>
            <TableCell>{user.role}</TableCell>
            <TableCell>{formatDate(user.created_at)}</TableCell>
            <TableCell className="flex items-center justify-end gap-4">
              <RelationActionButton
                type="button"
                onClick={() => handleEdit(user)}
              >
                Edit
              </RelationActionButton>
              <RelationActionButton
                type="button"
                className="text-red-600 hover:text-red-800"
                onClick={() => handleDelete(user)}
              >
                Delete
              </RelationActionButton>
            </TableCell>
          </>
        )}
      />

      <EditUserRoleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        user={editingUser}
        onSubmit={handleSubmit}
        isPending={updateMutation.isPending}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete User"
        description={
          deletingUser ? (
            <>
              Are you sure you want to delete{' '}
              <strong>
                {deletingUser.name} {deletingUser.surname}
              </strong>{' '}
              ({deletingUser.email})? This will remove all associated data
              including clinic relations, doctor relations, carts, invitations,
              and addresses.
            </>
          ) : (
            'Are you sure you want to delete this user?'
          )
        }
        onConfirm={handleConfirmDelete}
        isPending={deleteMutation.isPending}
      />
    </>
  );
}
